/* import __COLOCATED_TEMPLATE__ from './qa-review-status-filter.hbs'; */
/* RESPONSIBLE TEAM: team-fin-qa */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import type IntlService from 'embercom/services/intl';
import { isEmpty } from '@ember/utils';
import { type SelectedFilter } from 'embercom/services/filter-pattern-service';
import { type FilterBarItem } from 'embercom/services/filter-pattern-service';

interface Args {
  initiallyOpened: boolean;
  icon: string;
  type: string;
  name: string;
  label: string;
  onClose: () => void;
  selected: SelectedFilter;
  setSelected: (filterValues: string[], operator?: string) => void;
  analyticsObject: string;
  removeFilter: () => void;
  isDisabled: boolean;
}

interface Signature {
  Args: Args;
}

export enum ReviewStatus {
  Unreviewed = 'unreviewed',
  Submitted = 'submitted',
  PassedReview = 'passed_review',
  NeedsFix = 'needs_fix',
  WontFix = 'wont_fix',
  FixComplete = 'fix_complete',
  Any = REPORTING_FILTER_SELECT_ALL,
}

const ANALYTIC_NAME = 'qa_review_status';

export default class QaReviewStatusFilter extends Component<Signature> {
  @service declare appService: AppService;
  @service intercomEventService: any;
  @service declare intl: IntlService;
  @tracked label = this.currentLabel;
  @tracked selectedDropdownItems: string[] = this.args.selected.values || [];
  @tracked operator: string = this.args.selected.operator || 'category';

  get currentLabel() {
    if (isEmpty(this.selectedStatuses)) {
      return this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.qa-review-status.any',
      );
    } else {
      return this.selectedStatuses.map((value) => this.statusLabels[value]).join(', ');
    }
  }

  get selectedStatuses() {
    return this.selectedDropdownItems.filter((item) => item !== ReviewStatus.Any);
  }

  private get statusLabels(): Record<string, string> {
    return {
      [ReviewStatus.Unreviewed]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.qa-review-status.unreviewed',
      ),
      [ReviewStatus.Submitted]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.qa-review-status.submitted',
      ),
      [ReviewStatus.PassedReview]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.qa-review-status.passed-review',
      ),
      [ReviewStatus.NeedsFix]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.qa-review-status.needs-fix',
      ),
      [ReviewStatus.WontFix]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.qa-review-status.wont-fix',
      ),
      [ReviewStatus.FixComplete]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.qa-review-status.fix-complete',
      ),
    };
  }

  get allItems(): FilterBarItem[] {
    return Object.entries(this.statusLabels).map(([key, label]) => ({
      text: label,
      value: key,
      isSelected: this.selectedDropdownItems.includes(key),
    }));
  }

  @action
  trackAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: ANALYTIC_NAME,
      object: this.args.analyticsObject,
      operator: this.operator,
    });
  }

  @action
  onClose() {
    if (this.isDestroying) {
      return;
    }

    this.label = this.currentLabel;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::QaReviewStatusFilter': typeof QaReviewStatusFilter;
    'reporting/custom/chart-builder/filter-bar/qa-review-status-filter': typeof QaReviewStatusFilter;
  }
}
